import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { School } from '../../types';
import schoolService from '../../services/schoolService';

interface SchoolState {
  schools: School[];
  selectedSchool: School | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: SchoolState = {
  schools: [],
  selectedSchool: null,
  isLoading: false,
  error: null,
};

// Async thunks
export const fetchSchools = createAsyncThunk(
  'schools/fetchSchools',
  async (_, { rejectWithValue }) => {
    try {
      const response = await schoolService.getAllSchools();
      return response.data;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch schools');
    }
  }
);

export const createSchool = createAsyncThunk(
  'schools/createSchool',
  async (schoolData: Partial<School>, { rejectWithValue }) => {
    try {
      const response = await schoolService.createSchool(schoolData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to create school');
    }
  }
);

export const updateSchoolAsync = createAsyncThunk(
  'schools/updateSchool',
  async ({ id, schoolData }: { id: string; schoolData: Partial<School> }, { rejectWithValue }) => {
    try {
      const response = await schoolService.updateSchool(id, schoolData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to update school');
    }
  }
);

export const deleteSchoolAsync = createAsyncThunk(
  'schools/deleteSchool',
  async (id: string, { rejectWithValue }) => {
    try {
      await schoolService.deleteSchool(id);
      return id;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to delete school');
    }
  }
);

const schoolSlice = createSlice({
  name: 'schools',
  initialState,
  reducers: {
    setSchools: (state, action: PayloadAction<School[]>) => {
      state.schools = action.payload;
    },
    setSelectedSchool: (state, action: PayloadAction<School | null>) => {
      state.selectedSchool = action.payload;
    },
    addSchool: (state, action: PayloadAction<School>) => {
      state.schools.push(action.payload);
    },
    updateSchool: (state, action: PayloadAction<School>) => {
      const index = state.schools.findIndex(school => school.id === action.payload.id);
      if (index !== -1) {
        state.schools[index] = action.payload;
      }
    },
    removeSchool: (state, action: PayloadAction<string>) => {
      state.schools = state.schools.filter(school => school.id !== action.payload);
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch schools
      .addCase(fetchSchools.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSchools.fulfilled, (state, action) => {
        state.isLoading = false;
        state.schools = action.payload;
        state.error = null;
      })
      .addCase(fetchSchools.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Create school
      .addCase(createSchool.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createSchool.fulfilled, (state, action) => {
        state.isLoading = false;
        state.schools.push(action.payload);
        state.error = null;
      })
      .addCase(createSchool.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Update school
      .addCase(updateSchoolAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateSchoolAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.schools.findIndex(school => school.id === action.payload.id);
        if (index !== -1) {
          state.schools[index] = action.payload;
        }
        state.error = null;
      })
      .addCase(updateSchoolAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Delete school
      .addCase(deleteSchoolAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteSchoolAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.schools = state.schools.filter(school => school.id !== action.payload);
        state.error = null;
      })
      .addCase(deleteSchoolAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setSchools,
  setSelectedSchool,
  addSchool,
  updateSchool,
  removeSchool,
  setLoading,
  setError,
} = schoolSlice.actions;

export default schoolSlice.reducer; 